import {Linking, Pressable, View} from 'react-native'

import {atoms as a, useTheme} from '#/alf'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import {Text} from '#/components/Typography'
import type {StockSearchItem} from '#/state/queries/stock-search'

function formatPrice(price: number): string {
  return price.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatChange(change: number): string {
  const sign = change >= 0 ? '+' : ''
  return `${sign}${change.toFixed(2)}`
}

function formatPercent(pct: number): string {
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}

export function StockCard({item}: {item: StockSearchItem}) {
  const t = useTheme()
  const {
    state: pressed,
    onIn: onPressIn,
    onOut: onPressOut,
  } = useInteractionState()

  const isPositive = (item.change ?? 0) >= 0
  const changeColor = isPositive ? '#22c55e' : '#ef4444'

  const onPress = () => {
    Linking.openURL(
      `https://finance.yahoo.com/quote/${encodeURIComponent(item.symbol)}`,
    )
  }

  return (
    <Pressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      accessibilityRole="button"
      accessibilityLabel={`${item.symbol} - ${item.description}`}
      style={[
        a.flex_col,
        a.rounded_md,
        a.border,
        a.p_md,
        t.atoms.border_contrast_low,
        t.atoms.bg,
        {opacity: pressed ? 0.85 : 1},
      ]}>
      {/* Top row: symbol + price */}
      <View style={[a.flex_row, a.justify_between, a.align_center]}>
        <View style={[a.flex_1, a.pr_sm]}>
          <Text
            style={[a.text_lg, a.font_heavy, t.atoms.text]}
            numberOfLines={1}>
            {item.symbol}
          </Text>
          <Text
            style={[a.text_xs, t.atoms.text_contrast_medium, a.mt_2xs]}
            numberOfLines={1}>
            {item.description}
          </Text>
        </View>

        {item.price != null && (
          <View style={[a.align_end]}>
            <Text style={[a.text_lg, a.font_bold, t.atoms.text]}>
              {formatPrice(item.price)}
            </Text>
          </View>
        )}
      </View>

      {/* Change row */}
      {item.change != null && item.changePercent != null && (
        <View style={[a.flex_row, a.align_center, a.gap_sm, a.mt_sm]}>
          <View
            style={[
              a.rounded_sm,
              a.px_sm,
              a.py_2xs,
              {backgroundColor: changeColor},
            ]}>
            <Text style={[a.text_sm, a.font_bold, {color: '#fff'}]}>
              {formatPercent(item.changePercent)}
            </Text>
          </View>
          <Text style={[a.text_sm, {color: changeColor}]}>
            {formatChange(item.change)}
          </Text>
        </View>
      )}

      {/* Stats row */}
      {item.high != null && item.low != null && (
        <View
          style={[
            a.flex_row,
            a.gap_lg,
            a.mt_sm,
            a.pt_sm,
            a.border_t,
            t.atoms.border_contrast_low,
          ]}>
          <View>
            <Text style={[a.text_2xs, t.atoms.text_contrast_medium]}>
              Open
            </Text>
            <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text]}>
              {item.open != null ? formatPrice(item.open) : '—'}
            </Text>
          </View>
          <View>
            <Text style={[a.text_2xs, t.atoms.text_contrast_medium]}>
              High
            </Text>
            <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text]}>
              {formatPrice(item.high)}
            </Text>
          </View>
          <View>
            <Text style={[a.text_2xs, t.atoms.text_contrast_medium]}>
              Low
            </Text>
            <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text]}>
              {formatPrice(item.low)}
            </Text>
          </View>
          <View>
            <Text style={[a.text_2xs, t.atoms.text_contrast_medium]}>
              Prev Close
            </Text>
            <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text]}>
              {item.previousClose != null
                ? formatPrice(item.previousClose)
                : '—'}
            </Text>
          </View>
        </View>
      )}

      {/* Type badge */}
      <View style={[a.flex_row, a.align_center, a.mt_sm]}>
        <Text style={[a.text_2xs, t.atoms.text_contrast_medium]}>
          {item.type} · {item.exchange}
        </Text>
      </View>
    </Pressable>
  )
}
